import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import DashboardHeader from "../../../components/header/DashboardHeader";
import DashboardEmployerSidebar from "../../../components/header/DashboardSidebar";
import MobileMenu from "../../../components/header/MobileMenu";
import CopyrightFooter from "../../../components/footer/employers/CopyrightFooter";
import RecordsTableFilter from "../../../components/table-filter";
import CustomPagination from "../../../components/paginations";
import SmartTable from "../../../components/common/table-component";
import { callGetUserActionLogs } from "../../../api/settings/service";
import "./action-log.css";
import DatePicker from "react-datepicker";

const ACTIONS = ["", "LOGIN", "REFRESH_TOKEN", "LOGOUT", "VIEW", "CREATE", "UPDATE", "DELETE", "APPROVE", "REJECT", "CANCEL", "PAYMENT", "UPLOAD", "DOWNLOAD", "IMPORT", "EXPORT", "SEND", "ASSIGN", "UNASSIGN", "PERMISSION", "PASSWORD_CHANGE", "RESET", "UNLOCK", "LOCK", "GENERATE", "STATUS_CHANGE", "CALCULATE", "CHECK", "CLEAR", "ACTION"];

const ActionLogPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [request, setRequest] = useState({
        page: 1,
        maxResult: 20,
        keyword: "",
        userId: "",
        actionType: "",
        startDate: "",
        endDate: "",
    });
    const [rows, setRows] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecord, setTotalRecord] = useState(0);

    const loadData = async (override = {}) => {
        const payload = { ...request, ...override };
        const rsp = await dispatch(callGetUserActionLogs(payload));
        if (rsp?.payload?.responseCode === "00000") {
            const data = rsp.payload.data || {};
            setRows(data.result || []);
            setTotalPages(data.totalPages || 1);
            setTotalRecord(data.totalRecords || 0);
        } else {
            setRows([]);
            setTotalPages(1);
            setTotalRecord(0);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [request.page, request.maxResult]);

    const onChange = (e) => {
        const { name, value } = e.target;
        setRequest((prev) => ({ ...prev, [name]: value }));
    };

    const onSearch = (e) => {
        e?.preventDefault?.();
        if (request.page !== 1) {
            setRequest((prev) => ({ ...prev, page: 1 }));
        } else {
            loadData({ page: 1 });
        }
    };

    const resetSearch = () => {
        const next = {
            page: 1,
            maxResult: request.maxResult,
            keyword: "",
            userId: "",
            actionType: "",
            startDate: "",
            endDate: "",
        };
        setRequest(next);
        loadData(next);
    };

    const badgeClass = (action) => {
        switch (action) {
            case "CREATE":
            case "APPROVE":
            case "LOGIN":
            case "PAYMENT":
            case "ASSIGN": return "bg-success";
            case "UPDATE":
            case "STATUS_CHANGE":
            case "PASSWORD_CHANGE":
            case "RESET":
            case "UNLOCK":
            case "LOCK": return "bg-primary";
            case "DELETE":
            case "REJECT":
            case "CANCEL":
            case "UNASSIGN":
            case "CLEAR": return "bg-danger";
            case "EXPORT":
            case "IMPORT":
            case "UPLOAD":
            case "DOWNLOAD":
            case "SEND": return "bg-info text-dark";
            case "GENERATE":
            case "CALCULATE":
            case "CHECK":
            case "PERMISSION": return "bg-warning text-dark";
            case "REFRESH_TOKEN":
            case "LOGOUT":
            case "VIEW": return "bg-secondary";
            default: return "bg-dark";
        }
    };

    const prettyRequest = (value) => {
        if (!value) return "-";
        try { return JSON.stringify(JSON.parse(value), null, 2); }
        catch (_) { return String(value); }
    };

    const columns = useMemo(() => [
        {
            label: t("settings.actionLog.columns.dateTime"),
            key: "createdAt",
            render: (val) => val ? moment(val).format("DD/MM/YYYY HH:mm:ss") : "-",
        },
        {
            label: t("settings.actionLog.columns.user"),
            key: "userName",
            render: (val, row) => (
                <div>
                    <div className="fw-semibold">{val || row.username || `#${row.userId || "-"}`}</div>
                    <small className="text-muted">{t("settings.actionLog.userId")}: {row.userId || "-"}{row.roleCode ? ` • ${row.roleCode}` : ""}</small>
                </div>
            ),
        },
        {
            label: t("settings.actionLog.columns.action"),
            key: "actionType",
            render: (val) => <span className={`badge ${badgeClass(val)}`}>{t(`settings.actionLog.actionTypes.${val || "ACTION"}`)}</span>,
        },
        {
            label: t("settings.actionLog.columns.endpoint"),
            key: "endpoint",
            render: (val, row) => (
                <div className="action-log-endpoint">
                    <span className="badge bg-light text-dark me-1">{row.method || "-"}</span>
                    <code>{val || "-"}</code>
                </div>
            ),
        },
        {
            label: t("settings.actionLog.columns.result"),
            key: "status",
            render: (val, row) => (
                <div>
                    <span className={`badge ${Number(val) === 1 ? "bg-success" : "bg-danger"}`}>
                        {Number(val) === 1 ? t("settings.actionLog.success") : t("settings.actionLog.failed")}
                    </span>
                    <small className="d-block text-muted mt-1">{row.responseCode || row.httpStatus || "-"}</small>
                </div>
            ),
        },
        {
            label: t("settings.actionLog.columns.ip"),
            key: "ipAddress",
            render: (val) => val || "-",
        },
        {
            label: t("settings.actionLog.columns.detail"),
            key: "requestData",
            render: (val, row) => (
                <details className="action-log-details">
                    <summary className="btn btn-outline-secondary btn-sm">{t("settings.actionLog.view")}</summary>
                    <div className="action-log-detail-card">
                        <div><strong>{t("settings.actionLog.detail.response")}:</strong> {row.responseDescription || "-"}</div>
                        <div><strong>{t("settings.actionLog.detail.duration")}:</strong> {row.durationMs ?? 0} ms</div>
                        <div><strong>{t("settings.actionLog.detail.platform")}:</strong> {row.platform || "-"} {row.clientVersion ? `(${row.clientVersion})` : ""}</div>
                        <div><strong>{t("settings.actionLog.detail.userAgent")}:</strong> {row.userAgent || "-"}</div>
                        <div className="mt-2"><strong>{t("settings.actionLog.detail.request")}:</strong></div>
                        <pre>{prettyRequest(val)}</pre>
                    </div>
                </details>
            ),
        },
    ], [t]);

    return (
        <div className="page-wrapper dashboard">
            <span className="header-span"></span>
            <DashboardHeader />
            <MobileMenu />
            <DashboardEmployerSidebar />

            <section className="user-dashboard">
                <div className="dashboard-outer">
                    <div className="upper-title-box d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                            <h4>{t("settings.actionLog.title")}</h4>
                            <div className="text-muted">{t("settings.actionLog.subtitle")}</div>
                        </div>
                        <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
                            <i className="fas fa-arrow-left me-1"></i> {t("settings.actionLog.back")}
                        </button>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <div className="ls-widget">
                                <div className="tabs-box">
                                    <div className="widget-content p-4">
                                        <form onSubmit={onSearch}>
                                            <div className="row g-3 align-items-end">
                                                <div className="col-lg-3 col-md-6">
                                                    <label className="form-label">{t("settings.actionLog.keyword")}</label>
                                                    <input className="form-control-uld" name="keyword" value={request.keyword} onChange={onChange} placeholder={t("settings.actionLog.keywordPlaceholder")} />
                                                </div>
                                                <div className="col-lg-2 col-md-6">
                                                    <label className="form-label">{t("settings.actionLog.userId")}</label>
                                                    <input className="form-control-uld" name="userId" value={request.userId} onChange={onChange} placeholder={t("settings.actionLog.userId")} />
                                                </div>
                                                <div className="col-lg-2 col-md-6">
                                                    <label className="form-label">{t("settings.actionLog.action")}</label>
                                                    <select className="form-control-uld" name="actionType" value={request.actionType} onChange={onChange}>
                                                        {ACTIONS.map((item) => <option key={item || "ALL"} value={item}>{item ? t(`settings.actionLog.actionTypes.${item}`) : t("settings.actionLog.all")}</option>)}
                                                    </select>
                                                </div>
                                                <div className="col-lg-2 col-md-6">
                                                    <label className="form-label">{t("settings.actionLog.startDate")}</label>
                                                    <DatePicker
                                                        selected={request.startDate}
                                                        onChange={(date) => setRequest((value) => ({
                                                            ...value,
                                                            startDate: date,
                                                        }))}
                                                        dateFormat="dd/MM/yyyy"
                                                        placeholderText={t('settings.actionLog.startDate')}
                                                        className="form-control-uld"
                                                    />
                                                    {/* <input type="date" className="form-control-uld" name="startDate" value={request.startDate} onChange={onChange} /> */}
                                                </div>
                                                <div className="col-lg-2 col-md-6">
                                                    <label className="form-label">{t("settings.actionLog.endDate")}</label>
                                                    <DatePicker
                                                        selected={request.endDate}
                                                        onChange={(date) => setRequest((value) => ({
                                                            ...value,
                                                            endDate: date,
                                                        }))}
                                                        dateFormat="dd/MM/yyyy"
                                                        placeholderText={t('settings.actionLog.endDate')}
                                                        className="form-control-uld"
                                                    />
                                                    {/* <input type="date" className="form-control-uld" name="endDate" value={request.endDate} onChange={onChange} /> */}
                                                </div>
                                                <div className="col-auto d-flex gap-2">
                                                    <button type="submit" className="theme-btn btn-style-one">
                                                        <i className="fas fa-search me-1"></i> {t("button.search")}
                                                    </button>
                                                    <button type="button" className="btn btn-outline-secondary" onClick={resetSearch}>{t("button.clear")}</button>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <div className="ls-widget">
                                <div className="tabs-box">
                                    <div className="widget-content p-4">
                                        <RecordsTableFilter
                                            totalRecord={totalRecord}
                                            perPage={request.maxResult}
                                            onChange={(e) => setRequest((prev) => ({ ...prev, maxResult: Number(e.target.value), page: 1 }))}
                                        />

                                        <SmartTable
                                            columns={columns}
                                            data={{ result: rows }}
                                            rowKey="id"
                                            recordsTableFilter={false}
                                            customPagination={false}
                                            showRowNumber={true}
                                        />

                                        <CustomPagination
                                            currentPage={request.page}
                                            totalPages={totalPages}
                                            onPageChange={(page) => {
                                                if (page >= 1 && page <= totalPages) setRequest((prev) => ({ ...prev, page }));
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <CopyrightFooter />
            </section>
        </div>
    );
};

export default ActionLogPage;
